import { Link } from "wouter";

const NORFOLK_AI_LOGO =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663270229297/i88gywhNYafBWiKD3FZzYw/brand/norfolk-ai-logo_24f57943.png";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border/40 bg-background/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold tracking-tight text-foreground">
              Lola &amp; Ber · Redefining Normal™
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <Link href="/privacy" className="hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <Link href="/" className="hover:text-foreground transition-colors">
              ← Back to app
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
            Legal
          </p>
          <h1 className="text-4xl font-bold tracking-tight mb-3">Terms of Service</h1>
          <p className="text-muted-foreground text-sm">
            Effective date: <strong>July 1, 2026</strong> &nbsp;·&nbsp; Last updated:{" "}
            <strong>July 12, 2026</strong>
          </p>
        </div>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8 text-[15px] leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using the Lola &amp; Ber platform at{" "}
              <a href="https://lolandberretreats.com/" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4">lolandberretreats.com</a>{" "}
              ("the Platform"), you agree to be bound by these Terms of Service ("Terms"). If you
              do not agree to these Terms, you may not access or use the Platform.
            </p>
            <p className="mt-3">
              The Platform is operated by <strong>Norfolk Group LLC</strong> ("we", "us", or
              "our"), doing business as Norfolk AI.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Eligibility</h2>
            <p>
              Access to the Platform is by invitation only. You must be at least 18 years of age
              to use the Platform. By using the Platform, you represent and warrant that you meet
              this requirement and that all information you provide is accurate and complete.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. Your Account</h2>
            <p>
              You are responsible for maintaining the confidentiality of your login credentials and
              for all activity that occurs under your account. You agree to notify us immediately
              at{" "}
              <a href="https://lolandberretreats.com/contact/" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4">
                Contact Lola & Ber
              </a>{" "}
              if you suspect unauthorized access to your account.
            </p>
            <p className="mt-3">
              We reserve the right to suspend or terminate accounts that violate these Terms or
              that we reasonably believe pose a security risk.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Permitted Use</h2>
            <p>You may use the Platform solely for lawful purposes and in accordance with these Terms. You agree not to:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Share your login credentials with any third party.</li>
              <li>
                Attempt to gain unauthorized access to any portion of the Platform or its related
                systems.
              </li>
              <li>
                Scrape, copy, reproduce, or redistribute any content from the Platform without our
                express written permission.
              </li>
              <li>
                Use the Platform to transmit harmful, offensive, or unlawful content.
              </li>
              <li>
                Interfere with or disrupt the integrity or performance of the Platform.
              </li>
              <li>
                Use automated tools (bots, crawlers, scrapers) to access the Platform without
                prior written consent.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Intellectual Property</h2>
            <p>
              All content on the Platform — including but not limited to text, data, research,
              graphics, charts, radar visualizations, AI-generated insights, the Sage assistant,
              and the Lola &amp; Ber brand — is the exclusive property of Norfolk Group LLC or its
              licensors and is protected by applicable intellectual property laws.
            </p>
            <p className="mt-3">
              Nothing in these Terms grants you any right, title, or interest in any Platform
              content beyond the limited right to access and use the Platform as described herein.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. AI-Generated Content</h2>
            <p>
              The Platform includes Sage, an AI-powered research assistant. Sage's responses are
              generated by large language models and are provided for informational and research
              purposes only. They do not constitute professional advice (legal, financial, medical,
              or otherwise).
            </p>
            <p className="mt-3">
              AI-generated content may contain errors, omissions, or outdated information. You are
              solely responsible for evaluating and verifying any information provided by Sage
              before relying on it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Data and Research Content</h2>
            <p>
              The research data, forecasts, and demographic analyses presented on the Platform are
              based on publicly available sources and proprietary modeling. They are provided for
              informational purposes only and should not be construed as definitive statistical
              findings or professional research reports.
            </p>
            <p className="mt-3">
              We make no representations or warranties regarding the accuracy, completeness, or
              timeliness of any data or research content on the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Disclaimer of Warranties</h2>
            <p>
              THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className="mt-3">
              We do not warrant that the Platform will be uninterrupted, error-free, or free of
              viruses or other harmful components.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Limitation of Liability</h2>
            <p>
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, NORFOLK GROUP LLC SHALL NOT BE
              LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES
              ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE PLATFORM, EVEN IF
              WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>
            <p className="mt-3">
              OUR TOTAL LIABILITY TO YOU FOR ANY CLAIM ARISING OUT OF OR RELATED TO THESE TERMS
              OR THE PLATFORM SHALL NOT EXCEED ONE HUNDRED U.S. DOLLARS ($100).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Norfolk Group LLC and its
              officers, directors, employees, and agents from and against any claims, liabilities,
              damages, losses, and expenses (including reasonable attorneys' fees) arising out of
              or in any way connected with your access to or use of the Platform or your violation
              of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your access to the Platform at any time,
              with or without cause, and with or without notice. Upon termination, your right to
              use the Platform will immediately cease.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">12. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the
              State of <strong>Florida, United States</strong>, without regard to its conflict of
              law provisions. Any disputes arising under these Terms shall be subject to the
              exclusive jurisdiction of the courts located in Florida.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">13. Changes to These Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. When we do, we will update
              the "Last updated" date at the top of this page. Your continued use of the Platform
              after any changes constitutes your acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">14. Contact Us</h2>
            <p>
              If you have questions about these Terms, please contact us at:
            </p>
            <address className="not-italic mt-3 text-muted-foreground">
              <strong className="text-foreground">Norfolk Group LLC</strong>
              <br />
              Email:{" "}
              <a href="https://lolandberretreats.com/contact/" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4">
                Contact Lola & Ber
              </a>
            </address>
          </section>
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-border/40 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <span>© 2026 Norfolk Group LLC · Lola &amp; Ber · Redefining Normal™</span>
          <div className="flex items-center gap-4">
            <Link href="/privacy" className="hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <span>·</span>
            <Link href="/" className="hover:text-foreground transition-colors">
              Back to app
            </Link>
          </div>
        </footer>

        {/* Powered by Norfolk AI */}
        <div className="mt-8 flex items-center justify-center gap-2 opacity-50">
          <img src={NORFOLK_AI_LOGO} alt="Norfolk AI" className="h-4 w-4 object-contain" />
          <span className="text-xs text-muted-foreground">
            Powered by <span className="font-semibold">Norfolk AI</span>
          </span>
        </div>
      </main>
    </div>
  );
}
